import { useState, useCallback } from "react";

import { useBrandedKeywords } from "./use-branded-keywords";

interface IUseBrandedKeywordsPrepopulationParams {
    domain: string;
    selectedCountries: number[];
    hasManualKeywords: boolean;
    maxKeywordsAllowed: number;
    onKeywordsFetched: (keywords: string[]) => void;
}

interface IUseBrandedKeywordsPrepopulationReturn {
    isPrepopulating: boolean;
    brandedKeywords: string[];
    fetchBrandedKeywords: () => Promise<void>;
    resetPrepopulation: () => void;
}

export const useBrandedKeywordsPrepopulation = ({
    domain,
    selectedCountries,
    hasManualKeywords,
    maxKeywordsAllowed,
    onKeywordsFetched,
}: IUseBrandedKeywordsPrepopulationParams): IUseBrandedKeywordsPrepopulationReturn => {
    const [brandedKeywordsFetched, setBrandedKeywordsFetched] = useState(false);

    const { data: recommendedBrandedKeywords, isFetching, refetch } = useBrandedKeywords(domain, selectedCountries);

    const fetchBrandedKeywords = useCallback(async () => {
        if (brandedKeywordsFetched || selectedCountries.length === 0 || hasManualKeywords) {
            return;
        }

        setBrandedKeywordsFetched(true);

        const result = await refetch();

        if (result.data && result.data.length > 0) {
            const processedKeywords = result.data
                .map((keyword) => keyword.toLowerCase().trim())
                .filter((keyword) => keyword.length > 0);

            const trimmedKeywords =
                maxKeywordsAllowed === Infinity ? processedKeywords : processedKeywords.slice(0, maxKeywordsAllowed);

            onKeywordsFetched(trimmedKeywords);
        }
    }, [
        brandedKeywordsFetched,
        selectedCountries.length,
        hasManualKeywords,
        maxKeywordsAllowed,
        onKeywordsFetched,
        refetch,
    ]);

    const resetPrepopulation = useCallback(() => {
        setBrandedKeywordsFetched(false);
    }, []);

    return {
        isPrepopulating: isFetching,
        brandedKeywords: recommendedBrandedKeywords || [],
        fetchBrandedKeywords,
        resetPrepopulation,
    };
};
